import { useCases } from "../lib/cases";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { getService, services } from "../data/services";
import { useSEO, useJsonLd, breadcrumbLd } from "../lib/seo";
import { CTABanner } from "../components/Footer";
import { FadeUp, ParallaxImg } from "../components/motion";
import { useLang } from "../i18n";

const SubServiceCard = ({ n, t: title, d }) => {
  const { t, href } = useLang();
  return (
  <FadeUp delay={n * 0.05}>
    <div
      data-testid={`service-subservice-${n}`}
      className="group relative h-full overflow-hidden border border-white/10 bg-panel p-7 transition-all duration-500 hover:border-gold/40 sm:p-8"
    >
      <div className="card-highlight pointer-events-none absolute inset-0" aria-hidden="true" />
      <span className="font-mono text-xs text-gold">
        {String(n + 1).padStart(2, "0")}
      </span>
      <h3 className="mt-4 font-display text-lg font-semibold text-white sm:text-xl">{t(title)}</h3>
      <p className="mt-3 text-sm leading-relaxed text-mist">{t(d)}</p>
    </div>
  </FadeUp>
  );
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang, href, ov } = useLang();
  const service = getService(slug);
  const cases = useCases();
  const example = cases.find(c => c.slug === service?.relatedCase);
  const metaKey = service ? `${service.name} — meta` : "";
  const metaDesc = service ? (t(metaKey) !== metaKey ? t(metaKey) : t(service.tagline)) : "YUXEL services";
  useSEO(service ? `${t(service.name)} — YUXEL` : t("Services — YUXEL"), metaDesc);
  useJsonLd(
    "service",
    service && {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t(service.name),
      description: metaDesc,
      serviceType: t(service.name),
      provider: { "@type": "Organization", name: "YUXEL" },
      areaServed: "AZ",
      url: window.location.origin + href(`/services/${service.slug}`),
    }
  );
  useJsonLd(
    "breadcrumb",
    service &&
      breadcrumbLd([
        { name: "YUXEL", path: href("/") },
        { name: t("Services"), path: href("/services") },
        { name: t(service.name), path: href(`/services/${service.slug}`) },
      ])
  );

  if (!service) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-20 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">404</p>
        <h1 className="page-display-title mt-4 text-white">{t("Service not found")}</h1>
        <Link
          to={href("/services")}
          data-testid="service-detail-notfound-link"
          className="mt-8 rounded-full bg-gold px-6 py-3 font-semibold text-ink"
        >
          {t("View all services")}
        </Link>
      </div>
    );
  }

  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="service-detail-page">
      <header className="relative overflow-hidden pb-20 pt-36 sm:pb-28 sm:pt-44">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src={ov(`image:service:${service.slug}`, service.heroImg)}
            alt=""
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-ink/60" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <p className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold">
            <Link to={href("/services")} data-testid="service-detail-breadcrumb-link" className="hover:text-champagne">
              {t("Services")}
            </Link>
            <span className="text-white/40">/ {service.index}</span>
          </p>
          <h1 className="page-display-title max-w-5xl text-white">
            {t(service.name)}
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-mist sm:text-lg">
            {t(service.tagline)}
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8"><h2>{t("The business problem")}</h2><p className="mt-6 text-mist">{t(service.problem)}</p><h2 className="mt-12">{t("Commercial impact of the problem")}</h2><p className="mt-6 text-mist">{t(service.commercialImpact)}</p></section>
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-32" aria-label="Overview">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-3">
            <h2>{t("The system YUXEL builds")}</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-9">
            <p className="font-serif text-2xl font-medium leading-[1.4] text-white/90 sm:text-3xl">
              {t(service.overview)}
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-white/10 bg-panel" aria-label="What's included">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <FadeUp>
            <h2 className="font-serif text-3xl font-medium text-white sm:text-4xl">
              {t("What this discipline covers")}
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {service.subs.map((s, i) => (
              <SubServiceCard key={s.t} n={i} t={s.t} d={s.d} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8"><h2>{t("Measures of success")}</h2><p className="mt-6 text-mist">{t(service.measures)}</p><p className="mt-4 text-mist">{t("Targets and baselines are agreed after diagnosis; these are measurement criteria, not promised results.")}</p></section>

      <section className="border-t border-white/10 bg-panel" aria-label="Deliverables and process">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <div>
              <FadeUp>
                <h2 className="font-serif text-3xl font-medium text-white sm:text-4xl">
                  {t("Deliverables")}
                </h2>
              </FadeUp>
              <ul className="mt-10 space-y-4">
                {service.deliverables.map((d, i) => (
                  <FadeUp key={d} delay={i * 0.05}>
                    <li
                      data-testid={`service-deliverable-${i}`}
                      className="flex items-start gap-3 text-base text-white/85"
                    >
                      <Check className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
                      {t(d)}
                    </li>
                  </FadeUp>
                ))}
              </ul>
            </div>
            <div>
              <FadeUp>
                <h2 className="font-serif text-3xl font-medium text-white sm:text-4xl">
                  {t("How it runs")}
                </h2>
              </FadeUp>
              <div className="mt-10 space-y-8">
                {service.steps.map((st, i) => (
                  <FadeUp key={st.t} delay={i * 0.07}>
                    <div
                      data-testid={`service-step-${i}`}
                      className="relative border-l border-gold/40 pl-6"
                    >
                      <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-gold" aria-hidden="true" />
                      <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
                        {t("Step")} {i + 1}
                      </p>
                      <h3 className="mt-2 font-display text-xl font-semibold text-white">{t(st.t)}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-mist">{t(st.d)}</p>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8"><h2>{t("Relevant project")}</h2>{example ? <Link className="mt-6 inline-block underline" to={href('/work/'+example.slug)}>{example.client} — {t(example.title)}</Link> : <p className="mt-6 text-mist">{t("No approved public case is available for this service yet.")}</p>}</section>
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 sm:pb-32" aria-label="Related services">
        <FadeUp>
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-3xl font-medium text-white sm:text-4xl">
              {t("Often built alongside")}
            </h2>
            <Link
              to={href("/services")}
              data-testid="service-detail-all-services-link"
              className="hidden text-sm font-semibold text-gold underline-offset-4 hover:underline sm:block"
            >
              {t("All services")}
            </Link>
          </div>
        </FadeUp>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {others.map((o, i) => (
            <FadeUp key={o.slug} delay={i * 0.07}>
              <Link
                to={href(`/services/${o.slug}`)}
                data-testid={`service-related-card-${o.slug}`}
                className="group flex h-full flex-col justify-between border border-white/10 bg-panel p-7 transition-all duration-500 hover:border-gold/40"
              >
                <div>
                  <span className="font-mono text-xs text-gold">{o.index}</span>
                  <h3 className="mt-3 font-display text-xl font-semibold text-white group-hover:text-gold">
                    {t(o.name)}
                  </h3>
                  <p className="mt-2 text-sm text-mist">{t(o.tagline)}</p>
                </div>
                <ArrowUpRight className="mt-6 h-5 w-5 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-8" aria-label="Visual">
        <FadeUp>
          <ParallaxImg
            src={ov(`image:service:${service.slug}`, service.heroImg)}
            alt={`${t(service.name)} ${t("at YUXEL")}`}
            speed={7}
            className="aspect-[16/7] border border-white/10"
          />
        </FadeUp>
      </section>

      <CTABanner
        title={t("Ready to put %s to work?").replace("%s", t(service.name).toLocaleLowerCase(lang))}
        sub={t("Book a consultation — we'll map the constraint, the plan and the return before anything is built.")}
        testid={`service-cta-${service.slug}`}
      />
    </div>
  );
}
